import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CompositeFilterDescriptor, State, process } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { DataStateChangeEvent, GridDataResult, SelectAllCheckboxState, ColumnReorderEvent, ColumnVisibilityChangeEvent } from '@progress/kendo-angular-grid';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { TranslateService } from '@ngx-translate/core';
import { CBOPaymentsItemListService } from 'src/app/core/service/grid-data/grid-data-cbo-payments-item.service';
import { GridQueryUtils } from 'src/app/core/utility/grid-query-utils';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { Subscription } from 'rxjs';
import { GridSettings, GridResizeEvent } from 'src/app/core/model/grid-settings';
import { GridStatePersistingService } from 'src/app/core/service/grid-state-persisting';
import { SettingService } from 'src/app/core/service/setting.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { PayerBillingService } from 'src/app/core/service/payer-billing.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { CBOPaymentsService } from 'src/app/core/service/cbo-payments.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-cbo-payments-details',
  templateUrl: './cbo-payments-details.component.html',
  styleUrls: ['./cbo-payments-details.component.scss']
})

export class CBOPaymentsDetailsComponent implements OnInit, OnDestroy
{
  @Input() public cbo: any = null;
  @Input() public organizationPaymentStatuses: any = null;  
  @Input() public fromDateSelection: any = null;
  @Input() public toDateSelection: any = null;

  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count = 0;
  public pageSizes = [5, 10, 25, 50, 100, 1000];
  public view: GridViewManager;
  public selectedItem: any;
  public selectedRecordKeys: number[] = [];
  public filterText = '';
  public patientLabelText = '';
  public selectAllState: SelectAllCheckboxState = 'unchecked';
  public selectedOrgPayment: any;
  public changeOrgPaymentStatusDialogOpened = false;
  public orgPaymentStatuses = [];
  public isRevenueAnalystRole = false;
  public updateOrgPaymentForm: any;
  public invoiceItemStatuses = [];

  /**
   * CBO Payments list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'caseName', dir: 'asc' }
    ]
  };

  private listSubscription: Subscription;

  constructor(
    private appConfigService: AppConfigService,
    private translateService: TranslateService,
    private payerBillingService: PayerBillingService,
    private cboPaymentsItemListService: CBOPaymentsItemListService,
    private searchHelperService: SearchHelperService,
    private gridStatePersistingService: GridStatePersistingService,
    private settingService: SettingService,
    public intl: IntlService,
    private cboPaymentsService: CBOPaymentsService,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService,
    private authenticationService: AuthenticationService,
    private fb: UntypedFormBuilder,)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabelText = sessionSetting.patientLabel;
        // Get Org Payment Status
        this.dictionaryService.getCachedOrgPaymentStatus().subscribe({
          next: (data) =>
          {
            this.orgPaymentStatuses = data;
            this.isRevenueAnalystRole = this.authenticationService.currentUser.role === 'ROLE_CBO_REVENUE_ANALYST';
            this.initGridColumns();
          }
        });                
      }
    });
  }

  public get newOrgPaymentStatus(): UntypedFormControl
  {
    return this.updateOrgPaymentForm.get('newOrgPaymentStatus');
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.cboPaymentsItemListService ? this.cboPaymentsItemListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.invoiceItemStatuses = sortBy(this.payerBillingService.invoiceItemStatuses, ['label']);
    this.loadCBOPaymentItems();
  }

  /**
   * Perform grid refresh.
   */
  public refresh(): void
  {
    this.loadCBOPaymentItems();
  }

  /**
   * Selected org payment items.
   */
  public onSelectedKeysChange(event: any): void
  {
    this.selectedRecordKeys = event;
    if (this.selectedRecordKeys.length === 1)
    {
      this.selectedOrgPayment = this.gridView.data.filter(f => f.id == event[0])[0];
    }
    else
    {
      this.selectedOrgPayment = null;
    }
  }

  public onSelectAllChange(checkedState: SelectAllCheckboxState)
  {
    if (checkedState === 'checked')
    {
      this.selectedRecordKeys = this.gridView.data.map((item) => item.id);
      this.selectAllState = 'checked';
      this.selectedOrgPayment = null;
    }
    else
    {
      this.clearSelectedItems();
    }
  }

  public getSelectedCount(): number
  {
    return this.selectedRecordKeys.length;
  }

  public openChangeOrgPaymentStatusDialogOpened(): void
  {
    this.updateOrgPaymentForm = this.fb.group({
      newOrgPaymentStatus: [{ value: null, disabled: false }, { validators: [Validators.required] }]
    });
    this.changeOrgPaymentStatusDialogOpened = true;
  }

  public cancelChangeOrgPaymentStatus(): void
  {
    this.changeOrgPaymentStatusDialogOpened = false;
  }

  public changeOrgPaymentStatus()
  {
    // updates Org Payment Status for multiple case billable records at once
    this.cboPaymentsService.updateOrgPaymentStatus(this.updateOrgPaymentForm.get('newOrgPaymentStatus').value, this.selectedRecordKeys).subscribe({
      next: () =>
      {
        if (this.selectedRecordKeys.length === 1)
        {
          this.messageHandlerService.displaySuccess('ORG-PAYMENT-STATUS-CHANGED-SUCCESSFULLY');
        }
        else
        {
          this.messageHandlerService.displaySuccess('ORG-PAYMENTS-STATUS-CHANGED-SUCCESSFULLY');
        }

        this.changeOrgPaymentStatusDialogOpened = false;
        this.clearSelectedItems();
        this.state.skip = 0;
        this.executeQuery();
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'caseName');
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.executeQuery();
  }

  /**
    * Reorder grid column order event
    *
    * @param e ColumnReorderEvent event
    */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.view.columnsConfig.splice(e.oldIndex - 1, 1);
    this.view.columnsConfig.splice(e.newIndex - 1, 0, ...reorderedColumn);
    this.saveGrid();
  }

  /**
   * Grid onResize event.
   *
   * @param e Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
    this.saveGrid();
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
    this.saveGrid();
  }  

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.saveGrid();
    this.executeQuery();
  }

  /**
   * Reset grid columns with default state
   */
  public resetGridColumns(): void
  {
    this.gridStatePersistingService.delete('grid-settings-cbo-payment-item');
    this.initGridColumns();
    this.view.state.filter = { logic: 'and', filters: [] };
  }  

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  public allData(): Promise<ExcelExportData>
  {
    return new Promise((resolve, reject) =>
    {
      const currentTake = this.state.take;
      this.state.take = this.gridView.total;

      const httpPostBody = GridQueryUtils.normalizeFilters(this.state);

      this.cboPaymentsItemListService.query(
        (items: any) =>
        {
          if (items)
          {
            items.data.forEach(d =>
            {
              d.lastModifiedOn = this.intl.formatDate(new Date(d.lastModifiedOn), 'g');
              d.createdOn = this.intl.formatDate(new Date(d.createdOn), 'g');
              if (d.billableDate !== null && d.billableDate !== undefined && d.billableDate.length === 10)
              {
                d.billableDate = new Date(`${d.billableDate}T12:00:00.000`);
              }
              if (d.dateOfBirth !== null && d.dateOfBirth !== undefined && d.dateOfBirth.length === 10)
              {
                d.dateOfBirth = new Date(`${d.dateOfBirth}T12:00:00.000`);
              }              
              d.caseBillableStatusText = this.payerBillingService.getInvoiceItemStatusText(d.caseBillableStatus);
            });

            this.count = items.total;
            this.gridView = { data: items.data, total: items.total };
            this.view.setData(items.data);

            const result: ExcelExportData = {
              data: process(this.view.data, this.state).data
            };

            // Call allData() again with the original "take" value after resolving
            this.allDataWithOriginalTake(currentTake).then(() => resolve(result)).catch(reject);
          } else
          {
            reject(new Error("No data received"));
          }
        }, '', null, null, 'post', httpPostBody);
    });
  }

  private clearSelectedItems(): void
  {
    this.selectedRecordKeys = [];
    this.selectAllState = 'unchecked';
  }

  private allDataWithOriginalTake(originalTake: number): Promise<void>
  {
    return new Promise((resolve, reject) =>
    {
      this.state.take = originalTake; // Restore the original "take" value

      const httpPostBody = GridQueryUtils.normalizeFilters(this.state);

      this.cboPaymentsItemListService.query(
        (items: any) =>
        {
          if (items)
          {
            items.data.forEach(d =>
            {
              d.lastModifiedOn = this.intl.formatDate(new Date(d.lastModifiedOn), 'g');
              d.createdOn = this.intl.formatDate(new Date(d.createdOn), 'g');
              if (d.billableDate !== null && d.billableDate !== undefined && d.billableDate.length === 10)
              {
                d.billableDate = new Date(`${d.billableDate}T12:00:00.000`);
              }
              if (d.dateOfBirth !== null && d.dateOfBirth !== undefined && d.dateOfBirth.length === 10)
              {
                d.dateOfBirth = new Date(`${d.dateOfBirth}T12:00:00.000`);
              }              
              d.caseBillableStatusText = this.payerBillingService.getInvoiceItemStatusText(d.caseBillableStatus);
            });

            this.count = items.total;
            this.gridView = { data: items.data, total: items.total };
            this.view.setData(items.data);

            resolve();
          } else
          {
            reject(new Error("No data received"));
          }
        }, '', null, null, 'post', httpPostBody);
    });
  }

  private loadCBOPaymentItems(): void 
  {
    const toDate = new Date(this.toDateSelection.value.getFullYear(), this.toDateSelection.value.getMonth(), this.toDateSelection.value.getDate(), 12, 0, 0, 0);
    const fromDate = new Date(this.fromDateSelection.value.getFullYear(), this.fromDateSelection.value.getMonth(), this.fromDateSelection.value.getDate(), 12, 0, 0, 0);
    const statuses = this.organizationPaymentStatuses !== '' ? this.organizationPaymentStatuses.split(',') : '';
    
    // Load grid data
    let cboFilters: any[];
    if (statuses !== '')
    {
      cboFilters = [
        {
          logic: 'or',
          filters: statuses.map(id => ({
            field: 'orgPaymentStatusId',
            operator: 'eq',
            value: id
          }))
        }
      ];
      cboFilters.push({ logic: 'and', field: 'organizationId', operator: 'eq', value: this.cbo.organizationId });
      cboFilters.push({ logic: 'and', field: 'billableDate', operator: 'lte', value: toDate });
      cboFilters.push({ logic: 'and', field: 'billableDate', operator: 'gte', value: fromDate });    
    }
    else
    { 
      cboFilters = [
        { field: 'organizationId', operator: 'eq', value: this.cbo.organizationId },
        { field: 'billableDate', operator: 'lte', value: toDate },
        { field: 'billableDate', operator: 'gte', value: fromDate }
      ];      
    }

    this.state = {
      filter: {
        logic: 'and',
        filters: cboFilters
      }, group: [], skip: 0,
      sort: [
        { field: 'caseName', dir: 'asc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };
    
    this.executeQuery();
  }

  private executeQuery(): void
  {
    const httpPostBody = GridQueryUtils.normalizeFilters(this.state);
    this.cboPaymentsItemListService.query((items: any) =>
    {
      if (items !== null)
      {
        items.data.forEach(d =>
        {
          d.lastModifiedOn = this.intl.formatDate(new Date(d.lastModifiedOn), 'g');
          d.createdOn = this.intl.formatDate(new Date(d.createdOn), 'g');
          if (d.billableDate !== null && d.billableDate !== undefined && d.billableDate.length === 10)
          {
            d.billableDate = new Date(`${d.billableDate}T12:00:00.000`);
          }
          if (d.dateOfBirth !== null && d.dateOfBirth !== undefined && d.dateOfBirth.length === 10)
          {
            d.dateOfBirth = new Date(`${d.dateOfBirth}T12:00:00.000`);
          }          
          d.caseBillableStatusText = this.payerBillingService.getInvoiceItemStatusText(d.caseBillableStatus);
        });
      }
      this.count = items.total;
      this.gridView = { data: items.data, total: items.total };
      this.view.setData(items.data);
      this.allData = this.allData.bind(this);
    }, '', null, null, 'post', httpPostBody);
  }

  /**
   * Grid columns initialization
   */
  private initGridColumns(): void
  {
    this.translateService.get(['CASE', 'DATE-OF-SERVICE', 'ITEM', 'TOTAL-AMOUNT', 'UNITS', 'RATE', 'COORDINATOR', 'ORGANIZATION-NAME',
      'SERVICE-CODE', 'MODIFIER-CODE-1', 'MODIFIER-CODE-2', 'MODIFIER-CODE-3', 'DIAGNOSES', 'CASE-ID-NUMBER', 'STATUS', 'DATE-OF-BIRTH', 'ORG-PAYMENT-STATUS',
      'CREATED-BY', 'CREATED-DATE', 'LAST-MODIFIED-BY', 'LAST-MODIFIED-DATE']).subscribe({
        next: (res) =>
        {
          this.view.columnsConfig = [
            {
              field: 'caseId', title: res['CASE-ID-NUMBER'], titleKeys: ['CASE-ID-NUMBER'], filter: 'numeric', format: '#', hidden: false, groupNumber: 0, width: 60
            },
            {
              field: 'caseName', title: res.CASE, titleKeys: ['CASE'], hidden: false, width: 70, groupNumber: 0
            },
            {
              field: 'dateOfBirth', title: res['DATE-OF-BIRTH'], titleKeys: ['DATE-OF-BIRTH'], hidden: false, width: 60,
              format: '{0:d}', filter: 'date', groupNumber: 0
            },
            {
              field: 'billableDate', title: res['DATE-OF-SERVICE'], titleKeys: ['DATE-OF-SERVICE'], hidden: false, width: 70,
              format: '{0:d}', filter: 'date', groupNumber: 0
            },
            {
              field: 'billableName', title: res.ITEM, titleKeys: ['ITEM'], hidden: false, width: 110, groupNumber: 0
            },
            {
              field: 'serviceCode', title: res['SERVICE-CODE'], titleKeys: ['SERVICE-CODE'], hidden: false, width: 65, groupNumber: 0
            },
            {
              field: 'modifierCode', title: res['MODIFIER-CODE-1'], titleKeys: ['MODIFIER-CODE-1'], hidden: false, width: 70, groupNumber: 0
            },
            {
              field: 'modifierCode2', title: res['MODIFIER-CODE-2'], titleKeys: ['MODIFIER-CODE-2'], hidden: false, width: 70, groupNumber: 0
            },
            {
              field: 'modifierCode3', title: res['MODIFIER-CODE-3'], titleKeys: ['MODIFIER-CODE-3'], hidden: false, width: 70, groupNumber: 0
            },
            {
              field: 'diagnosisCodes', title: res.DIAGNOSES, titleKeys: ['DIAGNOSES'], hidden: false, width: 70, groupNumber: 0
            },
            {
              field: 'units', title: res.UNITS, titleKeys: ['UNITS'], filter: 'numeric', format: '{0:n}', hidden: false, width: 40, groupNumber: 0, class: 'text-right'
            },
            {
              field: 'rate', title: res.RATE, titleKeys: ['RATE'], filter: 'numeric', format: 'n2', hidden: false, width: 40, groupNumber: 0, class: 'text-right'
            },
            {
              field: 'totalAmount', title: res['TOTAL-AMOUNT'], titleKeys: ['TOTAL-AMOUNT'], filter: 'numeric', format: 'n2', hidden: false, width: 65, groupNumber: 0, class: 'text-right'
            },
            {
              field: 'coordinator', title: res.COORDINATOR, titleKeys: ['COORDINATOR'], hidden: false, width: 70, groupNumber: 0
            },
            {
              field: 'caseBillableStatus', title: res.STATUS, titleKeys: ['STATUS'], hidden: true, width: 40, groupNumber: 0
            },
            {
              field: 'orgPaymentStatus', title: res['ORG-PAYMENT-STATUS'], titleKeys: ['ORG-PAYMENT-STATUS'], hidden: false, width: 80, groupNumber: 0
            },
            { field: 'createdBy', title: res['CREATED-BY'], titleKeys: ['CREATED-BY'], hidden: true, width: 45, groupNumber: 0 },
            {
              field: 'createdOn', title: res['CREATED-DATE'], titleKeys: ['CREATED-DATE'], hidden: true, width: 70, 
              format: '{0:g}', filter: 'date', groupNumber: 0
            },
            { field: 'lastModifiedBy', title: res['LAST-MODIFIED-BY'], titleKeys: ['LAST-MODIFIED-BY'], hidden: true, width: 45, groupNumber: 0 },
            {
              field: 'lastModifiedOn', title: res['LAST-MODIFIED-DATE'], titleKeys: ['LAST-MODIFIED-DATE'], hidden: true, width: 70, 
              format: '{0:g}', filter: 'date', groupNumber: 0
            },            
          ];

          // If there is a previously saved grid settings, use it.
          const gridSettings: GridSettings = this.gridStatePersistingService.get('grid-settings-cbo-payment-item');
          if (gridSettings !== null)
          {
            const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translateService,
              JSON.stringify(gridSettings.columnsConfig), this.view.columnsConfig);
            this.view.columnsConfig = loadedColumns;

            if (gridSettings.state && gridSettings.state.filter && gridSettings.state.filter.filters)
            {
              GridQueryUtils.convertFiltersToJSDates(gridSettings.state.filter.filters, this.view.columnsConfig);
            }
            this.view.state = gridSettings.state;
          }
        }
      });
  }

  /**
   * Persit grid columns into storage.
   */
  private saveGrid(): void
  {
    const gridConfig: GridSettings = { columnsConfig: this.view.columnsConfig, state: this.view.state };
    this.gridStatePersistingService.set('grid-settings-cbo-payment-item', gridConfig);
  }
}
